import apiClient from './client';
import { ApiResponse } from '@/types';

/**
 * Developer metrics data
 */
export interface DeveloperMetric {
  _id: string;
  userId: {
    _id: string;
    username: string;
    displayName: string;
  };
  projectId?: {
    _id: string;
    name: string;
    code: string;
  };
  date: string;
  period: 'daily' | 'weekly' | 'monthly';
  commitCount: number;
  linesAdded: number;
  linesDeleted: number;
  filesChanged: number;
  reviewsReceived: number;
  avgReviewScore: number;
  standupsSubmitted: number;
  standupsMissed: number;
  activityScore: number;
  qualityScore: number;
  responsivenessScore: number;
  overallScore: number;
  isOutlier: boolean;
  outlierReasons: string[];
}

/**
 * Metrics overview
 */
export interface MetricsOverview {
  period: {
    start: string;
    end: string;
    days: number;
  };
  summary: {
    totalDevelopers: number;
    avgActivityScore: number;
    avgQualityScore: number;
    avgResponsivenessScore: number;
    avgOverallScore: number;
    outlierCount: number;
  };
  topPerformers: Array<{
    userId: string;
    username: string;
    displayName: string;
    overallScore: number;
  }>;
  needsAttention: Array<{
    userId: string;
    username: string;
    displayName: string;
    overallScore: number;
    reasons: string[];
  }>;
  scoreDistribution: {
    excellent: number;
    good: number;
    average: number;
    belowAverage: number;
    poor: number;
  };
}

/**
 * Leaderboard entry
 */
export interface LeaderboardEntry {
  rank: number;
  userId: string;
  username: string;
  displayName: string;
  overallScore: number;
  commitCount: number;
  avgReviewScore: number;
  standupRate: number;
  change?: 'up' | 'down' | 'same';
}

/**
 * Get user metrics
 */
export async function getUserMetrics(
  userId: string,
  params?: { period?: string; days?: number }
): Promise<DeveloperMetric[]> {
  const response = await apiClient.get<ApiResponse<DeveloperMetric[]>>(`/metrics/user/${userId}`, {
    params,
  });
  return response.data.data as DeveloperMetric[];
}

/**
 * Get team metrics
 */
export async function getTeamMetrics(
  projectId: string,
  params?: { period?: string; days?: number }
): Promise<DeveloperMetric[]> {
  const response = await apiClient.get<ApiResponse<DeveloperMetric[]>>(`/metrics/team/${projectId}`, {
    params,
  });
  return response.data.data as DeveloperMetric[];
}

/**
 * Get leaderboard
 */
export async function getLeaderboard(params?: {
  days?: number;
  projectId?: string;
  limit?: number;
}): Promise<LeaderboardEntry[]> {
  const response = await apiClient.get<ApiResponse<LeaderboardEntry[]>>('/metrics/leaderboard', {
    params,
  });
  return response.data.data as LeaderboardEntry[];
}

/**
 * Get metrics overview
 */
export async function getMetricsOverview(days = 30): Promise<MetricsOverview> {
  const response = await apiClient.get<ApiResponse<MetricsOverview>>('/metrics/overview', {
    params: { days },
  });
  return response.data.data as MetricsOverview;
}

/**
 * Trigger metrics calculation
 */
export async function calculateMetrics(): Promise<{ message: string }> {
  const response = await apiClient.post<ApiResponse<{ message: string }>>('/metrics/calculate');
  return response.data.data as { message: string };
}
